<template>
    <div class="container-fluid">
        <div class="mb-3">
            <BreadcrumbNav />
        </div>
        <!-- Currently Editing Banner -->
        <div v-if="instanceWorkgroupDetail && (instanceWorkgroupDetail.id || instanceWorkgroupDetail.name)" class="alert alert-success text-white d-flex align-items-center my-2" role="alert">
            <i class="material-icons me-2">edit</i>
            <div>
                <strong>Currently Editing (Instance Workgroup):</strong>
                <span v-if="instanceWorkgroupDetail.id" class="ms-2">Instance Workgroup ID: <strong>{{ instanceWorkgroupDetail.id }}</strong></span>
                <span v-if="instanceWorkgroupDetail.name" class="ms-2">- <strong>{{ instanceWorkgroupDetail.name }}</strong></span>
            </div>
        </div>

        <!-- Read-Only Banner -->
        <div v-if="readOnly" class="alert alert-warning text-white d-flex align-items-center my-2" role="alert">
            <i class="material-icons me-2">visibility</i>
            <div>
                <strong>View Only:</strong> You do not have permission to edit this instance workgroup.
            </div>
        </div>

        <!-- Tab Legend -->
        <div class="d-flex align-items-center gap-3 mb-2 text-xs text-secondary">
            <span><i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i> Required</span>
            <span><i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i> Optional</span>
        </div>

        <NavTabs>
            <NavTab target="workgroup-details" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Instance Workgroup Details (Required)">
                    Details <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>
            <NavTab target="add-remove" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Add/Remove Scheduled Persons (Required)">
                    Persons <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>
            <NavTab target="manage-admins" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Manage Instance Workgroup Admins (Required)">
                    Admins <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>
        </NavTabs>

        <ContentContainer>
            <template #content>
                <ExplainPageButton :page-key="`instance-workgroup.${activeTab}`" />
                <div v-if="activeTab === 'workgroup-details'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and edit the details and scheduling options for this instance workgroup.</p>
                    <WorkgroupDetails :readOnly="readOnly" :isWorkgroupAdmin="isEffectiveWorkgroupAdmin" />
                </div>

                <div v-else-if="activeTab === 'add-remove'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Add or remove persons scheduled for this instance workgroup.</p>
                    <AddRemove :readOnly="readOnly" />
                </div>

                <div v-else-if="activeTab === 'manage-admins'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Assign or remove administrators who can manage this instance workgroup.</p>
                    <ManageAdmins :readOnly="readOnlyForAdmins" />
                </div>
            </template>
        </ContentContainer>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue"
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import { usePermissions } from "@/composables/usePermissions"
import { useBreadcrumb } from "@/composables/useBreadcrumb"

import NavTab from "../../components/Tabs/NavTab.vue"
import NavTabs from "../../components/Tabs/NavTabs.vue"
import ContentContainer from "../../components/PageLayout/ContentContainer.vue"
import BreadcrumbNav from "../../components/common/BreadcrumbNav.vue"
import ExplainPageButton from "@/components/common/ExplainPageButton.vue"

import WorkgroupDetails from "./components/WorkgroupDetails.vue"
import AddRemove from "./components/AddRemove.vue"
import ManageAdmins from "./components/ManageAdmins.vue"

const store = useStore()
const route = useRoute()
const activeTab = ref("workgroup-details")
const instanceWorkgroupDetail = ref(null)
const instanceWorkgroupId = route.params.instanceworkgroupid

const { isReadOnlyForInstanceWorkgroup, isActivityWorkgroupAdmin, adminWorkgroupIds } = usePermissions()
const readOnly = computed(() => isReadOnlyForInstanceWorkgroup(instanceWorkgroupId))
// Workgroup admins can always manage IWG admins even if the rest is read-only.
// Fallback: if admin_level isn't returned by the API, check adminWorkgroupIds.
const isEffectiveWorkgroupAdmin = computed(() => isActivityWorkgroupAdmin.value || adminWorkgroupIds.value.length > 0)
const readOnlyForAdmins = computed(() => readOnly.value && !isEffectiveWorkgroupAdmin.value)
const { trail, setTrail } = useBreadcrumb()

// Watch for changes in the store
const storeInstanceWorkgroupDetail = computed(() => {
    return store.getters.instanceWorkgroupDetail || null
})

watch(storeInstanceWorkgroupDetail, (newValue) => {
    if (newValue) {
        instanceWorkgroupDetail.value = newValue
    }
}, { immediate: true })

onMounted(async () => {
    // Always fetch instance workgroup details to ensure data is loaded
    if (instanceWorkgroupId) {
        try {
            const data = await store.dispatch("getInstanceWorkgroupDetails", instanceWorkgroupId);
            if (data) {
                instanceWorkgroupDetail.value = data
                if (!trail.value.length) {
                    setTrail([
                        { label: 'Activities', icon: 'list', route: { name: 'activities-events' } },
                        { label: data.name || `Instance Workgroup #${instanceWorkgroupId}`, icon: 'groups' },
                    ])
                }
            }
        } catch (error) {
            console.error("Error loading instance workgroup:", error);
        }
    }
})
</script>
